import { useCallback, useEffect, useRef, useState } from 'react';
import { ChevronUp, ChevronDown, ChevronLeft, ChevronRight, ZoomIn, ZoomOut, Home, Save, Video, Pencil, Aperture, Sun, SunDim } from 'lucide-react';

/**
 * MobilePTZ — fjernstyring av PTZ-kamera fra mobil, hvor som helst.
 * Kommandoer gaar via skyserveren (ptz-relay) til bro-scriptet paa sendings-PC-en,
 * som sender VISCA over IP til kameraet paa lokalnettet.
 * URL: /ptz-mobil?key=<token>
 */

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SPEEDS = {
  1: { label: 'Rolig', pan: '04', tilt: '04', zoomIn: '8101040721FF', zoomOut: '8101040731FF' },
  2: { label: 'Normal', pan: '0C', tilt: '0C', zoomIn: '8101040724FF', zoomOut: '8101040734FF' },
  3: { label: 'Rask', pan: '18', tilt: '14', zoomIn: '8101040727FF', zoomOut: '8101040737FF' },
};

export default function MobilePTZ() {
  const key = new URLSearchParams(window.location.search).get('key') || '';
  const [bridgeOnline, setBridgeOnline] = useState(false);
  const [cloudOnline, setCloudOnline] = useState(true);
  const [speed, setSpeed] = useState(2);
  const [lastAction, setLastAction] = useState('');
  const [error, setError] = useState('');
  const [frame, setFrame] = useState(null);
  const [frameAge, setFrameAge] = useState(null);
  const [ackStatus, setAckStatus] = useState('idle'); // 'idle' | 'pending' | 'ok' | 'noack'
  const [ackIp, setAckIp] = useState('');
  const [presetMode, setPresetMode] = useState('recall'); // 'recall' | 'save' | 'name'
  const [presetNames, setPresetNames] = useState({});
  const [aeMode, setAeMode] = useState(() => localStorage.getItem('ptz-ae-mode') || ''); // '' | 'auto' | 'iris'
  useEffect(() => { if (aeMode) localStorage.setItem('ptz-ae-mode', aeMode); }, [aeMode]);
  const speedRef = useRef(2);
  useEffect(() => { speedRef.current = speed; }, [speed]);

  // Nettverkslogging til produksjonsloggen (throttlet pr. hendelsestype)
  const logThrottle = useRef({});
  const logNet = useCallback((event, detail, rtt) => {
    const now = Date.now();
    if (now - (logThrottle.current[event] || 0) < 5000) return;
    logThrottle.current[event] = now;
    try {
      const net = (navigator.connection && navigator.connection.effectiveType) || 'unknown';
      fetch(`${API}/ptz-relay/log`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token: key, event, detail: detail || '', net, rtt_ms: rtt != null ? Math.round(rtt) : null }),
      }).catch(() => {});
    } catch {}
  }, [key]);

  // Returfeed: WebSocket (~20 fps) med auto-reconnect + vaktbikkje for døde forbindelser
  useEffect(() => {
    if (!key) return;
    let alive = true;
    let ws = null;
    let lastWs = 0;
    let reconnectT = null;
    const wsUrl = `${process.env.REACT_APP_BACKEND_URL.replace(/^http/, 'ws')}/api/ws/ptz-frames?token=${encodeURIComponent(key)}&role=sub`;
    const connect = () => {
      if (!alive) return;
      try {
        ws = new WebSocket(wsUrl);
        ws.onmessage = (e) => {
          lastWs = Date.now();
          if (alive) { setFrame(e.data); setFrameAge(0); }
        };
        ws.onclose = () => {
          if (alive) { logNet('ws_close', 'returfeed lukket – kobler til igjen'); }
          if (alive && !reconnectT) reconnectT = setTimeout(() => { reconnectT = null; connect(); }, 1000);
        };
        ws.onerror = () => { try { ws.close(); } catch {} };
      } catch {
        if (alive && !reconnectT) reconnectT = setTimeout(() => { reconnectT = null; connect(); }, 1000);
      }
    };
    connect();
    // Vaktbikkje: hvis WS er "åpen" men ingen ramme på 4s, tving ny tilkobling
    const watchdog = setInterval(() => {
      if (ws && ws.readyState === 1 && lastWs && Date.now() - lastWs > 4000) {
        logNet('ws_stale', 'ingen returramme på >4s – tvinger reconnect');
        try { ws.close(); } catch {}
      }
    }, 1000);
    const poll = async () => {
      if (Date.now() - lastWs < 2500) return;
      try {
        const r = await fetch(`${API}/ptz-relay/frame?token=${encodeURIComponent(key)}`);
        if (!r.ok) return;
        const d = await r.json();
        if (alive && Date.now() - lastWs >= 2500) { setFrame(d.image); setFrameAge(d.age); }
      } catch {}
    };
    poll();
    const t = setInterval(poll, 500);
    return () => {
      alive = false;
      clearInterval(t); clearInterval(watchdog);
      if (reconnectT) clearTimeout(reconnectT);
      if (ws) try { ws.close(); } catch {}
    };
  }, [key, logNet]);

  // Heartbeat: sjekk forbindelsen hvert sekund. Skiller server (sky) fra produksjons-PC (bro).
  useEffect(() => {
    let alive = true;
    let wasCloud = true;
    let wasBridge = false;
    const check = async () => {
      const t0 = performance.now();
      try {
        const r = await fetch(`${API}/ptz-relay/status`, { signal: AbortSignal.timeout(2500) });
        const d = await r.json();
        const rtt = performance.now() - t0;
        if (!alive) return;
        setCloudOnline(true);
        setBridgeOnline(!!d.bridge_online);
        if (rtt > 1200) logNet('slow_rtt', 'treg statusrespons (mulig 4G-lugging)', rtt);
        if (!wasBridge && d.bridge_online) logNet('bridge_up', 'produksjons-PC tilkoblet igjen');
        if (wasBridge && !d.bridge_online) logNet('bridge_down', 'mistet produksjons-PC');
        wasCloud = true; wasBridge = !!d.bridge_online;
      } catch (e) {
        if (!alive) return;
        setCloudOnline(false); setBridgeOnline(false);
        if (wasCloud) logNet('cloud_down', `mistet server: ${e && e.name ? e.name : 'nettfeil'}`);
        wasCloud = false;
      }
    };
    check();
    const t = setInterval(check, 1000);
    return () => { alive = false; clearInterval(t); };
  }, [logNet]);

  // Hent lagrede navn på posisjonene
  useEffect(() => {
    if (!key) return;
    let alive = true;
    fetch(`${API}/ptz-relay/presets?token=${encodeURIComponent(key)}`)
      .then((r) => r.json())
      .then((d) => { if (alive) setPresetNames(d.names || {}); })
      .catch(() => {});
    return () => { alive = false; };
  }, [key]);

  const renamePreset = useCallback((n) => {
    const current = presetNames[n] || '';
    const name = (window.prompt(`Navn på posisjon ${n}:`, current) || '').trim().slice(0, 40);
    const next = { ...presetNames, [n]: name };
    setPresetNames(next);
    fetch(`${API}/ptz-relay/presets`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token: key, names: next }),
    }).catch(() => {});
  }, [presetNames, key]);

  const send = useCallback(async (hex, label) => {
    if (label) setLastAction(label);
    try {
      const r = await fetch(`${API}/ptz-relay/command`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token: key, hex }),
      });
      if (r.status === 403) { setError('Ugyldig lenke — be om ny URL fra regi'); return; }
      setError('');
      // Vent på kvittering fra bro-scriptet (bekrefter at kommandoen nådde kameraet)
      setAckStatus('pending');
      const sentAt = Date.now();
      let got = false;
      for (let i = 0; i < 6 && !got; i++) {
        await new Promise((res) => setTimeout(res, 400));
        try {
          const sr = await fetch(`${API}/ptz-relay/status`);
          const sd = await sr.json();
          const ack = sd.last_ack;
          if (ack && ack.age != null && ack.age * 1000 < Date.now() - sentAt + 800) {
            got = true;
            setAckIp(ack.ip || '');
            setAckStatus('ok');
          }
        } catch {}
      }
      if (!got) setAckStatus('noack');
    } catch {
      setError('Fikk ikke kontakt med serveren');
      setAckStatus('idle');
    }
  }, [key]);

  const stopMove = useCallback(() => send('8101060118180303FF', ''), [send]);
  const stopZoom = useCallback(() => send('8101040700FF', ''), [send]);

  // Lettvekts-send uten kvitteringssjekk (for gjentatte kommandoer ved hold)
  const sendRaw = useCallback((hex) => {
    fetch(`${API}/ptz-relay/command`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token: key, hex }),
    }).catch(() => {});
  }, [key]);

  // Blender (iris): trinnvis justering, gjentas mens knappen holdes inne
  const irisTimerRef = useRef(null);
  const startIris = useCallback((hex, label) => {
    send(hex, label);
    clearInterval(irisTimerRef.current);
    irisTimerRef.current = setInterval(() => sendRaw(hex), 350);
  }, [send, sendRaw]);
  const stopIris = useCallback(() => {
    clearInterval(irisTimerRef.current);
    irisTimerRef.current = null;
  }, []);

  const drive = useCallback((p, t, label) => {
    const s = SPEEDS[speedRef.current];
    send(`81010601${s.pan}${s.tilt}0${p}0${t}FF`, label);
  }, [send]);

  // Hold-inne-knapp: pointerdown starter, pointerup/leave stopper
  const HoldBtn = ({ onStart, onStop, children, testId, style }) => (
    <button
      data-testid={testId}
      onPointerDown={(e) => { e.preventDefault(); onStart(); }}
      onPointerUp={onStop}
      onPointerLeave={onStop}
      onPointerCancel={onStop}
      onContextMenu={(e) => e.preventDefault()}
      style={{
        border: '1px solid rgba(255,215,0,0.3)', borderRadius: '16px', cursor: 'pointer',
        backgroundColor: 'rgba(255,215,0,0.08)', color: '#FFD700',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        touchAction: 'none', WebkitUserSelect: 'none', userSelect: 'none',
        ...style,
      }}
    >
      {children}
    </button>
  );

  if (!key) {
    return (
      <div style={{
        minHeight: '100vh', backgroundColor: '#050F23', color: '#fff',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '30px', textAlign: 'center', fontFamily: "'Inter', sans-serif",
      }}>
        <div data-testid="ptz-mobil-nokey">
          <Video size={40} color="#FFD700" style={{ margin: '0 auto 14px' }} />
          <div style={{ fontSize: '1.1rem', fontWeight: 700, marginBottom: '8px' }}>Mangler nøkkel</div>
          <div style={{ fontSize: '0.85rem', color: 'rgba(255,255,255,0.55)', lineHeight: 1.5 }}>
            Denne siden må åpnes med lenken du fikk tilsendt
            (inneholder <span style={{ fontFamily: 'monospace' }}>?key=…</span>).
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh', backgroundColor: '#050F23', color: '#fff',
      fontFamily: "'Inter', sans-serif", padding: '16px 16px 36px',
      maxWidth: '480px', margin: '0 auto', boxSizing: 'border-box',
    }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '14px' }}>
        <div>
          <div style={{
            fontFamily: "'Barlow Condensed', sans-serif", fontSize: '1.4rem', fontWeight: 700,
            letterSpacing: '0.06em', textTransform: 'uppercase', color: '#FFD700',
          }}>
            PTZ Fjernstyring
          </div>
          <div style={{ fontSize: '0.72rem', color: 'rgba(255,255,255,0.45)' }}>Sørlandets Travpark · Seremoniplass</div>
        </div>
        <div
          data-testid="ptz-mobil-bridge-status"
          style={{
            display: 'flex', alignItems: 'center', gap: '6px', padding: '6px 10px',
            borderRadius: '999px', fontSize: '0.7rem', fontWeight: 600,
            backgroundColor: (cloudOnline && bridgeOnline) ? 'rgba(34,197,94,0.15)' : 'rgba(220,38,38,0.15)',
            border: `1px solid ${(cloudOnline && bridgeOnline) ? 'rgba(34,197,94,0.5)' : 'rgba(220,38,38,0.5)'}`,
            color: (cloudOnline && bridgeOnline) ? '#22C55E' : '#F87171',
          }}
        >
          <span style={{
            width: '8px', height: '8px', borderRadius: '50%',
            backgroundColor: (cloudOnline && bridgeOnline) ? '#22C55E' : '#DC2626',
            animation: (cloudOnline && bridgeOnline) ? 'ptzpulse 1.2s ease-in-out infinite' : 'none',
          }} />
          {!cloudOnline ? 'Ingen server' : bridgeOnline ? 'Tilkoblet' : 'PC frakoblet'}
        </div>
      </div>

      <style>{`@keyframes ptzpulse{0%,100%{opacity:1}50%{opacity:0.35}}`}</style>

      {!cloudOnline ? (
        <div data-testid="ptz-mobil-conn-banner" style={{
          fontSize: '0.72rem', color: '#FDE047', backgroundColor: 'rgba(234,179,8,0.1)',
          border: '1px solid rgba(234,179,8,0.35)', borderRadius: '10px', padding: '10px 12px',
          marginBottom: '14px', lineHeight: 1.5,
        }}>
          Mistet forbindelse til serveren — prøver å koble til igjen automatisk…
        </div>
      ) : !bridgeOnline ? (
        <div data-testid="ptz-mobil-conn-banner" style={{
          fontSize: '0.72rem', color: '#FCA5A5', backgroundColor: 'rgba(220,38,38,0.1)',
          border: '1px solid rgba(220,38,38,0.3)', borderRadius: '10px', padding: '10px 12px',
          marginBottom: '14px', lineHeight: 1.5,
        }}>
          Mistet forbindelse til produksjons-PC-en. Sjekk at bro-scriptet kjører der.
          Kommandoer når ikke kameraet nå.
        </div>
      ) : null}
      {error && (
        <div data-testid="ptz-mobil-error" style={{
          fontSize: '0.72rem', color: '#FCA5A5', marginBottom: '10px', textAlign: 'center',
        }}>
          {error}
        </div>
      )}

      {ackStatus !== 'idle' && (
        <div
          data-testid="ptz-mobil-ack"
          style={{
            fontSize: '0.72rem', marginBottom: '12px', padding: '8px 12px', borderRadius: '10px',
            lineHeight: 1.5, textAlign: 'center', fontWeight: 600,
            backgroundColor: ackStatus === 'ok' ? 'rgba(34,197,94,0.12)'
              : ackStatus === 'noack' ? 'rgba(234,179,8,0.12)' : 'rgba(255,255,255,0.06)',
            border: `1px solid ${ackStatus === 'ok' ? 'rgba(34,197,94,0.4)'
              : ackStatus === 'noack' ? 'rgba(234,179,8,0.4)' : 'rgba(255,255,255,0.12)'}`,
            color: ackStatus === 'ok' ? '#4ADE80'
              : ackStatus === 'noack' ? '#FDE047' : 'rgba(255,255,255,0.6)',
          }}
        >
          {ackStatus === 'pending' && 'Sender kommando…'}
          {ackStatus === 'ok' && `Kameraet mottok kommandoen ✓${ackIp ? ` → ${ackIp}` : ''}`}
          {ackStatus === 'noack' && 'Broen kvitterte ikke. Last ned og kjør nyeste bro-script (v9) på sendings-PC-en.'}
        </div>
      )}

      {/* Returfeed */}
      <div
        data-testid="ptz-mobil-return-feed"
        style={{
          position: 'relative', width: '100%', aspectRatio: '16/9',
          backgroundColor: '#000', borderRadius: '12px', overflow: 'hidden',
          marginBottom: '14px', border: '1px solid rgba(255,255,255,0.12)',
        }}
      >
        {frame && frameAge != null && frameAge < 6 ? (
          <>
            <img
              src={frame}
              alt="Returfeed"
              data-testid="ptz-mobil-return-img"
              style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'contain' }}
            />
            <div style={{
              position: 'absolute', top: '8px', left: '8px', display: 'flex', alignItems: 'center', gap: '5px',
              padding: '3px 8px', borderRadius: '999px', backgroundColor: 'rgba(0,0,0,0.55)',
              fontSize: '0.6rem', fontWeight: 700, color: '#22C55E', letterSpacing: '0.08em',
            }}>
              <span style={{ width: '6px', height: '6px', borderRadius: '50%', backgroundColor: '#22C55E' }} />
              RETURFEED
            </div>
          </>
        ) : (
          <div style={{
            position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column',
            alignItems: 'center', justifyContent: 'center', gap: '6px',
            color: 'rgba(255,255,255,0.35)', fontSize: '0.72rem', textAlign: 'center', padding: '0 20px',
          }}>
            <Video size={26} />
            Ingen returfeed — start videoen i kontrollpanelets Autotrack-fane
          </div>
        )}
      </div>

      {/* Hastighet */}
      <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
        {Object.entries(SPEEDS).map(([lvl, s]) => (
          <button
            key={lvl}
            data-testid={`ptz-mobil-speed-${lvl}`}
            onClick={() => setSpeed(parseInt(lvl, 10))}
            style={{
              flex: 1, padding: '10px', borderRadius: '999px', cursor: 'pointer',
              fontSize: '0.8rem', fontWeight: 700,
              border: speed === parseInt(lvl, 10) ? '1.5px solid #FFD700' : '1px solid rgba(255,255,255,0.15)',
              backgroundColor: speed === parseInt(lvl, 10) ? 'rgba(255,215,0,0.15)' : 'transparent',
              color: speed === parseInt(lvl, 10) ? '#FFD700' : 'rgba(255,255,255,0.55)',
            }}
          >
            {s.label}
          </button>
        ))}
      </div>

      {/* D-pad */}
      <div style={{
        display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '10px',
        marginBottom: '16px',
      }}>
        <div />
        <HoldBtn testId="ptz-mobil-up" onStart={() => drive(3, 1, 'Tilt opp')} onStop={stopMove} style={{ height: '84px' }}>
          <ChevronUp size={40} />
        </HoldBtn>
        <div />
        <HoldBtn testId="ptz-mobil-left" onStart={() => drive(1, 3, 'Pan venstre')} onStop={stopMove} style={{ height: '84px' }}>
          <ChevronLeft size={40} />
        </HoldBtn>
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: '0.62rem', color: 'rgba(255,255,255,0.35)', textAlign: 'center', lineHeight: 1.4,
        }}>
          Hold inne<br />for å styre
        </div>
        <HoldBtn testId="ptz-mobil-right" onStart={() => drive(2, 3, 'Pan høyre')} onStop={stopMove} style={{ height: '84px' }}>
          <ChevronRight size={40} />
        </HoldBtn>
        <div />
        <HoldBtn testId="ptz-mobil-down" onStart={() => drive(3, 2, 'Tilt ned')} onStop={stopMove} style={{ height: '84px' }}>
          <ChevronDown size={40} />
        </HoldBtn>
        <div />
      </div>

      {/* Zoom */}
      <div style={{ display: 'flex', gap: '10px', marginBottom: '20px' }}>
        <HoldBtn
          testId="ptz-mobil-zoomout"
          onStart={() => { setLastAction('Zoom ut'); send(SPEEDS[speedRef.current].zoomOut, ''); }}
          onStop={stopZoom}
          style={{ flex: 1, height: '64px', gap: '8px', fontSize: '0.85rem', fontWeight: 700 }}
        >
          <ZoomOut size={26} /> Zoom ut
        </HoldBtn>
        <HoldBtn
          testId="ptz-mobil-zoomin"
          onStart={() => { setLastAction('Zoom inn'); send(SPEEDS[speedRef.current].zoomIn, ''); }}
          onStop={stopZoom}
          style={{ flex: 1, height: '64px', gap: '8px', fontSize: '0.85rem', fontWeight: 700 }}
        >
          <ZoomIn size={26} /> Zoom inn
        </HoldBtn>
      </div>

      {/* Blender (iris) */}
      <div style={{ marginBottom: '20px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
          <span style={{ fontSize: '0.78rem', fontWeight: 700, color: 'rgba(255,255,255,0.7)', letterSpacing: '0.04em', display: 'flex', alignItems: 'center', gap: '6px' }}>
            <Aperture size={15} color="#5CE0D6" /> Blender
          </span>
          {/* Eksponeringsveksler: Auto <-> Blender (iris-prioritet) */}
          <div style={{ display: 'flex', gap: '0', borderRadius: '999px', overflow: 'hidden', border: '1px solid rgba(255,255,255,0.15)' }}>
            <button
              data-testid="ptz-mobil-ae-auto"
              onClick={() => { send('8101043900FF', 'Eksponering: Auto'); setAeMode('auto'); }}
              style={{
                padding: '6px 12px', cursor: 'pointer', fontSize: '0.7rem', fontWeight: 700, border: 'none',
                backgroundColor: aeMode === 'auto' ? 'rgba(255,215,0,0.2)' : 'transparent',
                color: aeMode === 'auto' ? '#FFD700' : 'rgba(255,255,255,0.55)',
              }}
            >
              Auto
            </button>
            <button
              data-testid="ptz-mobil-ae-iris"
              onClick={() => { send('810104390BFF', 'Eksponering: Blender'); setAeMode('iris'); }}
              style={{
                padding: '6px 12px', cursor: 'pointer', fontSize: '0.7rem', fontWeight: 700, border: 'none',
                backgroundColor: aeMode === 'iris' ? 'rgba(92,224,214,0.2)' : 'transparent',
                color: aeMode === 'iris' ? '#5CE0D6' : 'rgba(255,255,255,0.55)',
              }}
            >
              Blender
            </button>
          </div>
        </div>
        <div style={{ display: 'flex', gap: '10px', opacity: aeMode === 'auto' ? 0.4 : 1, pointerEvents: aeMode === 'auto' ? 'none' : 'auto' }}>
          <HoldBtn
            testId="ptz-mobil-iris-down"
            onStart={() => startIris('8101040B03FF', 'Blender – mørkere')}
            onStop={stopIris}
            style={{ flex: 1, height: '60px', gap: '8px', fontSize: '0.85rem', fontWeight: 700, borderColor: 'rgba(92,224,214,0.3)', backgroundColor: 'rgba(92,224,214,0.08)', color: '#5CE0D6' }}
          >
            <SunDim size={24} /> Mørkere
          </HoldBtn>
          <HoldBtn
            testId="ptz-mobil-iris-up"
            onStart={() => startIris('8101040B02FF', 'Blender – lysere')}
            onStop={stopIris}
            style={{ flex: 1, height: '60px', gap: '8px', fontSize: '0.85rem', fontWeight: 700, borderColor: 'rgba(92,224,214,0.3)', backgroundColor: 'rgba(92,224,214,0.08)', color: '#5CE0D6' }}
          >
            <Sun size={24} /> Lysere
          </HoldBtn>
        </div>
        <div style={{ fontSize: '0.64rem', color: 'rgba(255,255,255,0.4)', marginTop: '6px', textAlign: 'center' }}>
          {aeMode === 'auto'
            ? 'Kameraet står i Auto — bytt til «Blender» for å justere manuelt'
            : 'Hold inne for å justere blenderen (krever «Blender»-eksponering)'}
        </div>
      </div>

      {/* Lagrede posisjoner (VISCA-presets 1–4) */}
      <div style={{ marginBottom: '16px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
          <span style={{ fontSize: '0.78rem', fontWeight: 700, color: 'rgba(255,255,255,0.7)', letterSpacing: '0.04em' }}>
            Lagrede posisjoner
          </span>
          <div style={{ display: 'flex', gap: '6px' }}>
            <button
              data-testid="ptz-mobil-save-mode"
              onClick={() => setPresetMode((m) => (m === 'save' ? 'recall' : 'save'))}
              style={{
                display: 'flex', alignItems: 'center', gap: '5px', padding: '6px 11px', borderRadius: '999px',
                cursor: 'pointer', fontSize: '0.72rem', fontWeight: 700,
                border: presetMode === 'save' ? '1.5px solid #FBBF24' : '1px solid rgba(255,255,255,0.2)',
                backgroundColor: presetMode === 'save' ? 'rgba(251,191,36,0.15)' : 'transparent',
                color: presetMode === 'save' ? '#FBBF24' : 'rgba(255,255,255,0.6)',
              }}
            >
              <Save size={14} /> Lagre
            </button>
            <button
              data-testid="ptz-mobil-name-mode"
              onClick={() => setPresetMode((m) => (m === 'name' ? 'recall' : 'name'))}
              style={{
                display: 'flex', alignItems: 'center', gap: '5px', padding: '6px 11px', borderRadius: '999px',
                cursor: 'pointer', fontSize: '0.72rem', fontWeight: 700,
                border: presetMode === 'name' ? '1.5px solid #5CE0D6' : '1px solid rgba(255,255,255,0.2)',
                backgroundColor: presetMode === 'name' ? 'rgba(92,224,214,0.15)' : 'transparent',
                color: presetMode === 'name' ? '#5CE0D6' : 'rgba(255,255,255,0.6)',
              }}
            >
              <Pencil size={13} /> Navn
            </button>
          </div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '8px' }}>
          {[1, 2, 3, 4].map((n) => {
            const name = presetNames[n];
            const active = presetMode === 'save' ? '#FBBF24' : presetMode === 'name' ? '#5CE0D6' : '#5CE0D6';
            const border = presetMode === 'save' ? 'rgba(251,191,36,0.6)' : presetMode === 'name' ? 'rgba(92,224,214,0.6)' : 'rgba(92,224,214,0.4)';
            const bg = presetMode === 'save' ? 'rgba(251,191,36,0.1)' : presetMode === 'name' ? 'rgba(92,224,214,0.08)' : 'rgba(92,224,214,0.1)';
            return (
              <button
                key={n}
                data-testid={`ptz-mobil-preset-${n}`}
                onClick={() => {
                  if (presetMode === 'save') {
                    if (window.confirm(`Lagre nåværende posisjon som "${name || `Posisjon ${n}`}"?`)) {
                      send(`8101043F010${n}FF`, `${name || `Posisjon ${n}`} lagret`);
                    }
                    setPresetMode('recall');
                  } else if (presetMode === 'name') {
                    renamePreset(n);
                    setPresetMode('recall');
                  } else {
                    send(`8101043F020${n}FF`, `Henter ${name || `posisjon ${n}`}`);
                  }
                }}
                style={{
                  minHeight: '62px', padding: '6px 4px', borderRadius: '12px', cursor: 'pointer',
                  display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '2px',
                  border: `1px solid ${border}`, backgroundColor: bg, color: active,
                }}
              >
                <span style={{
                  fontSize: '0.6rem', fontWeight: 700, opacity: 0.6,
                }}>{n}</span>
                <span style={{
                  fontSize: name ? '0.72rem' : '0.9rem', fontWeight: 800, lineHeight: 1.1,
                  wordBreak: 'break-word', textAlign: 'center',
                }}>
                  {name || `Pos ${n}`}
                </span>
              </button>
            );
          })}
        </div>
        <div style={{ fontSize: '0.66rem', color: 'rgba(255,255,255,0.4)', marginTop: '6px', textAlign: 'center' }}>
          {presetMode === 'save'
            ? 'Trykk en plass for å lagre nåværende posisjon'
            : presetMode === 'name'
            ? 'Trykk en plass for å gi den et navn'
            : 'Trykk for å hente en lagret posisjon'}
        </div>
      </div>

      {/* Bordposisjon */}
      <div style={{ display: 'flex', gap: '10px', marginBottom: '16px' }}>
        <button
          data-testid="ptz-mobil-goto-home"
          onClick={() => send('8101043F0200FF', 'Kjører til bordposisjon')}
          style={{
            flex: 2, padding: '14px', borderRadius: '12px', cursor: 'pointer',
            border: 'none', backgroundColor: '#5CE0D6', color: '#050F23',
            fontSize: '0.9rem', fontWeight: 700, display: 'flex', alignItems: 'center',
            justifyContent: 'center', gap: '8px',
          }}
        >
          <Home size={18} /> Gå til bordposisjon
        </button>
        <button
          data-testid="ptz-mobil-save-home"
          onClick={() => {
            if (window.confirm('Lagre nåværende posisjon som bordposisjon?')) {
              send('8101043F0100FF', 'Bordposisjon lagret');
            }
          }}
          style={{
            flex: 1, padding: '14px', borderRadius: '12px', cursor: 'pointer',
            border: '1px solid rgba(255,255,255,0.2)', backgroundColor: 'transparent',
            color: 'rgba(255,255,255,0.7)', fontSize: '0.8rem', fontWeight: 600,
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
          }}
        >
          <Save size={16} /> Lagre
        </button>
      </div>

      {lastAction && (
        <div data-testid="ptz-mobil-last-action" style={{
          textAlign: 'center', fontSize: '0.72rem', color: 'rgba(255,255,255,0.45)',
          fontFamily: "'JetBrains Mono', monospace",
        }}>
          Siste: {lastAction}
        </div>
      )}
    </div>
  );
}
